'use client';

import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import type { Dictionary } from '@/lib/i18n/dictionaries/dictionary.types';
import type { Locale } from '@/lib/i18n/i18n.constants';

type LocaleContextValue = {
    locale: Locale;
    dictionary: Dictionary;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * Carries the request's locale and its UI dictionary to Client Components.
 *
 * The dictionary travels through the RSC payload instead of being imported in
 * the components: a Client Component importing `getDictionary` would pull
 * *every* language into the browser chunk, which grows with each locale added.
 */
export function LocaleProvider({ locale, dictionary, children }: LocaleContextValue & { children: ReactNode }) {
    return <LocaleContext.Provider value={{ locale, dictionary }}>{children}</LocaleContext.Provider>;
}

function useLocaleContext(): LocaleContextValue {
    const ctx = useContext(LocaleContext);
    if (!ctx) {
        throw new Error('useLocale must be used within LocaleProvider');
    }
    return ctx;
}

export function useLocale(): Locale {
    return useLocaleContext().locale;
}

/** UI chrome strings for the current locale (accessible names, buttons, consent copy). */
export function useDictionary(): Dictionary {
    return useLocaleContext().dictionary;
}
